<template>
  <Modal position="end" ref="modalRef">
    <template #header>Reschedule showing</template>
    <template #body>
      <RescheduleShowingForm v-model:form="form" />
    </template>

    <template #footer>
      <Button variant="ghost" @click="close">Cancel</Button>
      <Button :loading="isLoading" @click="reschedule">Reschedule</Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { helpers, required } from '@vuelidate/validators';
import { ref } from 'vue';

import { Button, Modal, useAlertsStore, useForm } from '@tallo/design-system';
import { useAvailabilityStore } from '@tallo/investor/availability';
import { useNavigation } from '@tallo/investor/navigation';

import { useRescheduleShowing } from '../mutations/reschedule-showing.mutation';
import { getShowingTimeRange } from '../utils/get-showing-time-range.util';
import RescheduleShowingForm from './reschedule-showing-form.vue';

const alertStore = useAlertsStore();
const { openShowingDetails } = useNavigation();
const { mutateAsync, isLoading } = useRescheduleShowing();

const props = defineProps<{ showingId: string; showingAgentId?: string }>();

const availabilityStore = useAvailabilityStore();

const modalRef = ref<InstanceType<typeof Modal> | null>(null);
const form = useForm<{ showingAgentId: string; comment: string; date: Date | undefined; time: string | undefined }>(
  {
    date: undefined,
    showingAgentId: props.showingAgentId as string,
    comment: '',
    time: '8:00 am',
  },
  {
    date: { required: helpers.withMessage('You have not selected the date', required) },
    time: { required: helpers.withMessage('Time is required', required) },
    showingAgentId: { required: helpers.withMessage('Showing agent is required', required) },
  },
);

function open() {
  modalRef.value?.show();
}

function close() {
  modalRef.value?.close();
}

function reschedule() {
  form.submit(async () => {
    const { startTime, endTime } = getShowingTimeRange(
      form.fields.time as string,
      form.fields.date as Date,
      availabilityStore.getShowingDuration!,
    );
    const rescheduleShowingDto = {
      showingAgentId: form.fields.showingAgentId,
      comment: form.fields.comment || null,
      startTime,
      endTime,
    };

    try {
      const { newShowingId } = await mutateAsync({ showingId: props.showingId, rescheduleShowingDto });

      close();
      openShowingDetails(newShowingId, true);
    } catch (error) {
      alertStore.catchError(error);
    }
  });
}

defineExpose({
  open,
});
</script>
